import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VideocamIcon from "@mui/icons-material/Videocam";

import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	mediaBox: {
		display: "inline-block",
		width: "fit-content",
		maxWidth: "min(360px, 78vw)",
		"& > div": {
			width: "auto !important",
			maxWidth: "100% !important",
		},
		"& img": {
			display: "block",
			width: "100% !important",
			height: "auto !important",
			maxWidth: "100%",
			borderRadius: 8,
			objectFit: "cover",
		},
		[theme.breakpoints.down("sm")]: {
			maxWidth: "min(300px, 82vw)",
		},
	},
	modalPaper: {
		backgroundColor: "rgba(0,0,0,0.9)",
		boxShadow: "none",
		maxWidth: "none",
	},
	modalImageWrap: {
		position: "relative",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: "100%",
		padding: theme.spacing(2),
		boxSizing: "border-box",
	},
	modalImage: {
		maxWidth: "92vw",
		maxHeight: "88vh",
		width: "auto",
		height: "auto",
		objectFit: "contain",
		borderRadius: 6,
	},
	closeButton: {
		position: "absolute",
		top: theme.spacing(1),
		right: theme.spacing(1),
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.35)",
		"&:hover": {
			backgroundColor: "rgba(0,0,0,0.55)",
		},
	},
	downloadButton: {
		position: "absolute",
		top: theme.spacing(1),
		right: theme.spacing(7.5),
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.35)",
		"&:hover": {
			backgroundColor: "rgba(0,0,0,0.55)",
		},
	},
	messageMedia: {
		width: "100%",
		maxWidth: "100%",
		height: "auto",
		display: "block",
		objectFit: "cover",
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},
	videoMedia: {
		width: "100%",
		maxWidth: "100%",
		height: "auto",
		display: "block",
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},
	videoThumbWrap: {
		position: "relative",
		display: "block",
		cursor: "pointer",
		overflow: "hidden",
		borderRadius: 8,
		backgroundColor: "#111b21",
	},
	videoOverlay: {
		position: "absolute",
		inset: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		pointerEvents: "none",
		background:
			"linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
	},
	videoPlayButton: {
		width: 54,
		height: 54,
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.48)",
		boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
		"& svg": {
			fontSize: "2.2rem",
		},
	},
	videoBadge: {
		position: "absolute",
		left: 8,
		bottom: 8,
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		padding: "3px 7px",
		borderRadius: 999,
		fontSize: "0.72rem",
		fontWeight: 600,
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.55)",
		pointerEvents: "none",
		"& svg": {
			fontSize: "0.9rem",
		},
	},
	modalVideo: {
		maxWidth: "92vw",
		maxHeight: "88vh",
		width: "auto",
		height: "auto",
		borderRadius: 6,
	},
}));

const getFileNameFromUrl = (url, fallback = "archivo") => {
	if (!url) return fallback;

	try {
		const cleanUrl = url.split("?")[0];
		const fileName = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
		return fileName || fallback;
	} catch (error) {
		return fallback;
	}
};

const ModalImageCors = ({ imageUrl, mediaType = "image", fileName }) => {
	const classes = useStyles();
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!imageUrl) return;
		let mounted = true;
		const fetchImage = async () => {
			try {
				setFetching(true);
				const { data, headers } = await api.get(imageUrl, {
					responseType: "blob",
				});
				if (!mounted) return;
				const url = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				setBlobUrl(url);
			} finally {
				if (mounted) setFetching(false);
			}
		};
		fetchImage();

		return () => {
			mounted = false;
		};
	}, [imageUrl]);

	useEffect(() => {
		return () => {
			if (blobUrl) {
				window.URL.revokeObjectURL(blobUrl);
			}
		};
	}, [blobUrl]);

	const displayUrl = fetching ? imageUrl : blobUrl || imageUrl;
	const isVideo = mediaType === "video";
	const downloadName = fileName || getFileNameFromUrl(imageUrl, isVideo ? "video" : "imagen");
	const handleClose = () => setOpen(false);
	const stopPropagation = (event) => event.stopPropagation();

	return (
		<>
			<div className={classes.mediaBox}>
				{isVideo ? (
					<div className={classes.videoThumbWrap} onClick={() => setOpen(true)}>
						<video
							className={classes.videoMedia}
							src={displayUrl}
							preload="metadata"
						/>
						<div className={classes.videoOverlay}>
							<span className={classes.videoPlayButton}>
								<PlayArrowIcon />
							</span>
						</div>
						<span className={classes.videoBadge}>
							<VideocamIcon />
							Video
						</span>
					</div>
				) : (
					<img
						src={displayUrl}
						alt="image"
						className={classes.messageMedia}
						onClick={() => setOpen(true)}
					/>
				)}
			</div>
			<Dialog
				open={open}
				onClose={handleClose}
				fullScreen
				PaperProps={{ className: classes.modalPaper }}
			>
				<div className={classes.modalImageWrap} onClick={handleClose}>
					<IconButton
						component="a"
						href={displayUrl}
						download={downloadName}
						target="_blank"
						rel="noopener noreferrer"
						className={classes.downloadButton}
						size="large"
						onClick={stopPropagation}
					>
						<DownloadIcon />
					</IconButton>
					<IconButton
						onClick={handleClose}
						className={classes.closeButton}
						size="large"
					>
						<CloseIcon />
					</IconButton>
					{isVideo ? (
						<video
							src={displayUrl}
							className={classes.modalVideo}
							controls
							autoPlay
							onClick={stopPropagation}
						/>
					) : (
						<img
							src={displayUrl}
							alt="image-full"
							className={classes.modalImage}
							onClick={stopPropagation}
						/>
					)}
				</div>
			</Dialog>
		</>
	);
};

export default ModalImageCors;
